import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Button,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import { Add, Close, Repeat } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Reservation } from './types';
import { formatReservationTime } from './formatTime';
import { isSeriesReservation } from './ReservationBlock';
import { focusVisibleSx, reservationOwnSx, reservationOtherSx } from './stiklainisStyles';

interface StiklainisDayAgendaProps {
  open: boolean;
  day: Date | null;
  reservations: Reservation[];
  currentUserId?: string;
  /** When true, render as inline panel (mobile under-grid) instead of dialog/drawer */
  inline?: boolean;
  onClose: () => void;
  onReservationClick: (reservation: Reservation, isOwn: boolean) => void;
  onCreate: (day: Date) => void;
}

const StiklainisDayAgenda: React.FC<StiklainisDayAgendaProps> = ({
  open,
  day,
  reservations,
  currentUserId,
  inline = false,
  onClose,
  onReservationClick,
  onCreate,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!day) return null;

  const dayLabel = day.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const sorted = [...reservations].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  const body = (
    <>
      <List dense disablePadding>
        {sorted.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 0.5 }}>
            {t('stiklainis.noReservationsThisDay')}
          </Typography>
        )}
        {sorted.map((r) => {
          const isOwn = Boolean(currentUserId && r.user.id === currentUserId);
          const organizer = `${r.user.firstName} ${r.user.lastName}`;
          const timeRange = `${formatReservationTime(r.startTime)} – ${formatReservationTime(r.endTime)}`;
          const recurring = isSeriesReservation(r);
          return (
            <ListItemButton
              key={r.id}
              onClick={() => onReservationClick(r, isOwn)}
              aria-label={[r.title, timeRange, organizer].join(', ')}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                alignItems: 'flex-start',
                ...(isOwn ? reservationOwnSx(theme) : reservationOtherSx(theme)),
                ...focusVisibleSx,
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {recurring && <Repeat sx={{ fontSize: 14 }} aria-hidden />}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {r.title}
                    </Typography>
                  </Box>
                }
                secondary={
                  <>
                    <Typography variant="caption" display="block">
                      {timeRange}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {t('stiklainis.organizer')}: {organizer}
                    </Typography>
                  </>
                }
              />
            </ListItemButton>
          );
        })}
      </List>
    </>
  );

  const actions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', width: '100%' }}>
      {!inline && (
        <Button onClick={onClose} sx={focusVisibleSx}>
          {t('common.close')}
        </Button>
      )}
      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => onCreate(day)}
        aria-label={t('stiklainis.newReservation')}
        sx={focusVisibleSx}
      >
        {t('stiklainis.newReservation')}
      </Button>
    </Box>
  );

  if (inline) {
    if (!open) return null;
    return (
      <Box
        sx={{
          mt: 1.5,
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          bgcolor: 'background.paper',
        }}
        aria-label={t('stiklainis.dayAgendaTitle')}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          {dayLabel}
        </Typography>
        {body}
        <Box sx={{ mt: 1 }}>{actions}</Box>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12, maxHeight: '75vh' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            {dayLabel}
          </Typography>
          <IconButton onClick={onClose} aria-label={t('common.close')} size="small" sx={focusVisibleSx}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 2, overflow: 'auto' }}>{body}</Box>
        <Box sx={{ px: 2, pb: 2 }}>{actions}</Box>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{dayLabel}</DialogTitle>
      <DialogContent dividers>{body}</DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>{actions}</DialogActions>
    </Dialog>
  );
};

export default StiklainisDayAgenda;
